// src/pages/SurveysList.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Typography,
  Tooltip,
  FormControlLabel,
  Switch,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  getDoc,
  getCountFromServer,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { debug, info, warn, error } from "../logger";
import PageHeader from "../components/PageHeader";
import SurveyHelpBanner from "../components/SurveyHelpBanner";
import { getAppVersion } from "../utils/version";

type SurveyStatus = "unpublished" | "published" | "deleted";
type Role = "owner" | "editor" | "viewer" | "admin";

interface Survey {
  id: string;
  title: string;
  updatedAt?: Date;
  status: SurveyStatus;
  json?: any;
  userId?: string; // owner uid
  roles?: Record<string, "owner" | "editor" | "viewer">;
  memberIds?: string[];
  effectiveRole?: Role | null; // computed for current user
  responseCount?: number;
}

type TestDataConfig = {
  enabled: boolean;
  surveyTemplateIds: string[];
  listTemplateIds: string[];
};

const TEST_DATA_CONFIG_DOC_ID = "testData";

const normalizeTestDataConfig = (raw: any): TestDataConfig => ({
  enabled: raw?.enabled === true,
  surveyTemplateIds: Array.isArray(raw?.surveyTemplateIds)
    ? raw.surveyTemplateIds.filter((id: unknown): id is string => typeof id === "string")
    : [],
  listTemplateIds: Array.isArray(raw?.listTemplateIds)
    ? raw.listTemplateIds.filter((id: unknown): id is string => typeof id === "string")
    : [],
});

function normalizeNameKey(name: string): string {
  return name.trim().toLocaleLowerCase();
}

function pickUniqueName(baseName: string, taken: Set<string>): string {
  const trimmed = baseName.trim();
  const base = trimmed || "Untitled";
  let candidate = base;
  let i = 1;
  while (taken.has(normalizeNameKey(candidate))) {
    candidate = `${base} (${i})`;
    i += 1;
  }
  taken.add(normalizeNameKey(candidate));
  return candidate;
}

function remapSurveyListBindings(input: any, listIdMap: Record<string, string>): any {
  if (Array.isArray(input)) {
    return input.map((item) => remapSurveyListBindings(item, listIdMap));
  }
  if (!input || typeof input !== "object") return input;

  const out: Record<string, any> = {};
  Object.entries(input).forEach(([key, value]) => {
    if ((key === "dataListId" || key === "firestoreListId") && typeof value === "string" && listIdMap[value]) {
      out[key] = listIdMap[value];
      return;
    }
    out[key] = remapSurveyListBindings(value, listIdMap);
  });
  return out;
}

export default function SurveyManagerList() {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [newName, setNewName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAll, setShowAll] = useState(false); // admin-only scope
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState<Survey | null>(null);
  const [duplicateName, setDuplicateName] = useState("");
  const [includeResponses, setIncludeResponses] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateErr, setDuplicateErr] = useState<string | null>(null);
  const [testDataConfig, setTestDataConfig] = useState<TestDataConfig>({
    enabled: false,
    surveyTemplateIds: [],
    listTemplateIds: [],
  });
  const [populateBusy, setPopulateBusy] = useState(false);
  const [populateErr, setPopulateErr] = useState<string | null>(null);
  const [populateMsg, setPopulateMsg] = useState<string | null>(null);

  const navigate = useNavigate();
  const appVersion = getAppVersion();

  // Load admin flag from /users/{uid}
  useEffect(() => {
    async function loadRole() {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        const role = snap.exists() ? (snap.data() as any)?.accountRole : undefined;
        setIsAdmin(role === "admin");
      } catch (e) {
        debug("SurveyManagerList: loadRole fallback (non-admin).", e);
        setIsAdmin(false);
      }
    }
    loadRole();
  }, []);

  useEffect(() => {
    async function loadTestDataConfig() {
      const user = auth.currentUser;
      if (!user) {
        setTestDataConfig({ enabled: false, surveyTemplateIds: [], listTemplateIds: [] });
        return;
      }

      try {
        const cfgRef = doc(db, "adminSettings", TEST_DATA_CONFIG_DOC_ID);
        const cfgSnap = await getDoc(cfgRef);
        if (!cfgSnap.exists()) {
          setTestDataConfig({ enabled: false, surveyTemplateIds: [], listTemplateIds: [] });
          return;
        }
        setTestDataConfig(normalizeTestDataConfig(cfgSnap.data() as any));
      } catch (e) {
        warn("SurveyManagerList: failed to load test data config", e);
        setTestDataConfig({ enabled: false, surveyTemplateIds: [], listTemplateIds: [] });
      }
    }
    loadTestDataConfig();
  }, []);

  // Title-case for UI
  const labelForRole = (r: Role | null | undefined) =>
    r === "owner" ? "Owner" :
    r === "editor" ? "Editor" :
    r === "viewer" ? "Viewer" :
    r === "admin"  ? "Admin"  : "";

  // Compute effective role for a survey row
  const computeEffectiveRole = (row: Survey, uid: string): Role | null => {
    if (!uid) return null;
    if (row.userId === uid) return "owner";
    if (isAdmin && showAll) return "admin"; // admin viewing via Show all
    const r = row.roles?.[uid];
    if (r === "editor" || r === "viewer" || r === "owner") return r;
    if (Array.isArray(row.memberIds) && row.memberIds.includes(uid)) return "viewer";
    return null;
  };

  // Fetch surveys (role-aware)
  useEffect(() => {
    async function fetchSurveys() {
      const user = auth.currentUser;
      if (!user) {
        warn("SurveyManagerList: no authenticated user, skipping fetch");
        setSurveys([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setErrMsg(null);

      try {
        let rows: Survey[] = [];

        if (isAdmin && showAll) {
          // Admin: show every survey (no orderBy; sort client-side)
          const snapAll = await getDocs(collection(db, "surveys"));
          rows = snapAll.docs.map((d) => {
            const v = d.data() as any;
            const asDate = v.updatedAt?.toDate?.();
            const status: SurveyStatus =
              (v.status as SurveyStatus) ??
              (v.published ? "published" : "unpublished");
            return {
              id: d.id,
              title: v.title || "(Untitled)",
              updatedAt: asDate,
              status,
              json: v.json || {},
              userId: v.userId,
              roles: v.roles,
              memberIds: v.memberIds,
            };
          });
        } else {
          // Non-admin (or admin in personal scope): own + member
          const userId = user.uid;

          const ownQ = query(collection(db, "surveys"), where("userId", "==", userId));
          const ownSnap = await getDocs(ownQ);

          const memberQ = query(
            collection(db, "surveys"),
            where("memberIds", "array-contains", userId)
          );
          const memberSnap = await getDocs(memberQ);

          const map = new Map<string, Survey>();
          const pushDoc = (d: any) => {
            const v = d.data() as any;
            const asDate = v.updatedAt?.toDate?.();
            const status: SurveyStatus =
              (v.status as SurveyStatus) ??
              (v.published ? "published" : "unpublished");
            map.set(d.id, {
              id: d.id,
              title: v.title || "(Untitled)",
              updatedAt: asDate,
              status,
              json: v.json || {},
              userId: v.userId,
              roles: v.roles,
              memberIds: v.memberIds,
            });
          };
          ownSnap.docs.forEach(pushDoc);
          memberSnap.docs.forEach(pushDoc);

          rows = Array.from(map.values());
        }

        // Hide deleted
        rows = rows.filter((r) => r.status !== "deleted");

        // Fetch response counts (aggregate without downloading documents)
        try {
          rows = await Promise.all(
            rows.map(async (r) => {
              try {
                const totalSnap = await getCountFromServer(
                  collection(db, "surveys", r.id, "responses")
                );
                const deletedSnap = await getCountFromServer(
                  query(collection(db, "surveys", r.id, "responses"), where("deleted", "==", true))
                );
                const total = totalSnap.data().count || 0;
                const deleted = deletedSnap.data().count || 0;
                const activeCount = Math.max(0, total - deleted);
                return { ...r, responseCount: activeCount };
              } catch (countErr) {
                warn("SurveyManagerList: response count failed for", r.id, countErr);
                return r;
              }
            })
          );
        } catch (countAllErr) {
          warn("SurveyManagerList: response count batch failed", countAllErr);
        }

        // Compute effective role and sort
        const uid = auth.currentUser?.uid || "";
        rows = rows
          .map((r) => ({ ...r, effectiveRole: computeEffectiveRole(r, uid) }))
          .sort((a, b) => {
            const ta = a.updatedAt?.getTime?.() ?? 0;
            const tb = b.updatedAt?.getTime?.() ?? 0;
            return tb - ta;
          });

        setSurveys(rows);
        info("SurveyManagerList: loaded", rows.length, "surveys");
      } catch (e: any) {
        error("SurveyManagerList: fetchSurveys failed:", e);
        setErrMsg(e?.message || "Failed to load surveys.");
        setSurveys([]);
      } finally {
        setLoading(false);
      }
    }

    fetchSurveys();
  }, [isAdmin, showAll]);

  // Create & navigate (seed roles + memberIds with owner)
  const handleCreate = async () => {
    const name = newName.trim();
    const user = auth.currentUser;
    if (!user || !name) return;
    try {
      const ref = await addDoc(collection(db, "surveys"), {
        userId: user.uid,
        ownerId: user.uid,
        title: name,
        json: { title: name, pages: [] },
        status: "unpublished",
        published: false, // legacy
        roles: { [user.uid]: "owner" },
        memberIds: [user.uid],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      navigate(`/surveys/${ref.id}/edit`);
    } catch (e) {
      error("SurveyManagerList: create failed:", e);
    }
  };

  // Soft delete
  const handleDelete = async (id: string) => {
    if (!window.confirm("Move this survey to Deleted Items?")) return;
    try {
      await updateDoc(doc(db, "surveys", id), {
        status: "deleted",
        published: false,
        updatedAt: serverTimestamp(),
      });
      setSurveys((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      error("SurveyManagerList: soft delete failed for", id, e);
    }
  };

  // Duplicate survey (owner-only; admin extendable later)
  const openDuplicateDialog = (survey: Survey) => {
    setDuplicateTarget(survey);
    setDuplicateName(`Copy: ${survey.title || "Untitled"}`);
    setIncludeResponses(false);
    setDuplicateErr(null);
    setDuplicateOpen(true);
  };

  const handleDuplicate = async () => {
    const user = auth.currentUser;
    if (!user || !duplicateTarget) return;
    setDuplicating(true);
    setDuplicateErr(null);
    try {
      const srcRef = doc(db, "surveys", duplicateTarget.id);
      const srcSnap = await getDoc(srcRef);
      if (!srcSnap.exists()) throw new Error("Original survey not found.");
      const data = srcSnap.data() as any;

      const newTitle = duplicateName.trim() || `Copy: ${data.title || "Untitled"}`;
      const payload = {
        userId: user.uid,
        title: newTitle,
        json: data.json || {},
        status: "unpublished",
        published: false,
        roles: { [user.uid]: "owner" },
        memberIds: [user.uid],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        clonedFrom: duplicateTarget.id,
      };

      const newRef = await addDoc(collection(db, "surveys"), payload);
      let copiedResponses = 0;

      if (includeResponses) {
        const resSnap = await getDocs(collection(db, "surveys", duplicateTarget.id, "responses"));
        const activeResponses = resSnap.docs.filter((d) => (d.data() as any)?.deleted !== true);
        copiedResponses = activeResponses.length;
        await Promise.all(
          activeResponses.map((d) =>
            setDoc(doc(db, "surveys", newRef.id, "responses", d.id), d.data())
          )
        );
      }

      const uid = user.uid;
      const created: Survey = {
        id: newRef.id,
        title: newTitle,
        json: data.json || {},
        status: "unpublished",
        updatedAt: new Date(),
        userId: user.uid,
        roles: { [user.uid]: "owner" },
        memberIds: [user.uid],
        responseCount: copiedResponses,
      };
      created.effectiveRole = computeEffectiveRole(created, uid);

      setSurveys((prev) =>
        [created, ...prev].sort((a, b) => {
          const ta = a.updatedAt?.getTime?.() ?? 0;
          const tb = b.updatedAt?.getTime?.() ?? 0;
          return tb - ta;
        })
      );

      setDuplicateOpen(false);
      setDuplicateTarget(null);
    } catch (e: any) {
      error("SurveyManagerList: duplicate failed", e);
      setDuplicateErr(e?.message || "Failed to duplicate survey.");
    } finally {
      setDuplicating(false);
    }
  };

  const handlePopulateTestData = async () => {
    const user = auth.currentUser;
    if (!user) return;

    setPopulateErr(null);
    setPopulateMsg(null);

    if (!window.confirm("Populate your account with the configured test surveys and lists?")) {
      return;
    }

    setPopulateBusy(true);
    try {
      const cfgSnap = await getDoc(doc(db, "adminSettings", TEST_DATA_CONFIG_DOC_ID));
      const cfg = cfgSnap.exists()
        ? normalizeTestDataConfig(cfgSnap.data() as any)
        : { enabled: false, surveyTemplateIds: [], listTemplateIds: [] };
      setTestDataConfig(cfg);

      if (!cfg.enabled) {
        throw new Error("Test data population is currently disabled by an admin.");
      }
      if (!cfg.surveyTemplateIds.length && !cfg.listTemplateIds.length) {
        throw new Error("No survey or list templates are currently configured.");
      }

      const uid = user.uid;
      const listIdMap: Record<string, string> = {};
      let copiedLists = 0;
      let copiedListItems = 0;
      let copiedSurveys = 0;
      const createdSurveys: Survey[] = [];

      const [existingOwnedListsSnap, existingOwnedSurveysSnap] = await Promise.all([
        getDocs(query(collection(db, "lists"), where("userId", "==", uid))),
        getDocs(query(collection(db, "surveys"), where("userId", "==", uid))),
      ]);

      const takenListNames = new Set<string>();
      existingOwnedListsSnap.docs.forEach((d) => {
        const data = d.data() as any;
        const name = (data?.name as string | undefined)?.trim();
        if (name) takenListNames.add(normalizeNameKey(name));
      });

      const takenSurveyNames = new Set<string>();
      existingOwnedSurveysSnap.docs.forEach((d) => {
        const data = d.data() as any;
        const title = (data?.title as string | undefined)?.trim();
        if (title) takenSurveyNames.add(normalizeNameKey(title));
      });

      for (const srcListId of cfg.listTemplateIds) {
        const srcListSnap = await getDoc(doc(db, "lists", srcListId));
        if (!srcListSnap.exists()) continue;
        const srcList = srcListSnap.data() as any;
        if ((srcList?.status ?? "active") === "deleted") continue;

        const newListName = pickUniqueName(srcList?.name || "Untitled list", takenListNames);
        const newListRef = await addDoc(collection(db, "lists"), {
          userId: uid,
          ownerId: uid,
          name: newListName,
          status: "active",
          roles: { [uid]: "owner" },
          memberIds: [uid],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          clonedFrom: srcListId,
          clonedFromTemplate: true,
        });
        listIdMap[srcListId] = newListRef.id;
        copiedLists += 1;

        const srcItemsSnap = await getDocs(collection(db, "lists", srcListId, "items"));
        if (!srcItemsSnap.empty) {
          let batch = writeBatch(db);
          let opCount = 0;

          for (const srcItem of srcItemsSnap.docs) {
            const srcItemData = srcItem.data() as any;
            const dstItemRef = doc(collection(db, "lists", newListRef.id, "items"));
            batch.set(dstItemRef, {
              ...srcItemData,
              userId: uid,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            opCount += 1;
            copiedListItems += 1;

            if (opCount >= 450) {
              await batch.commit();
              batch = writeBatch(db);
              opCount = 0;
            }
          }

          if (opCount > 0) {
            await batch.commit();
          }
        }
      }

      for (const srcSurveyId of cfg.surveyTemplateIds) {
        const srcSurveySnap = await getDoc(doc(db, "surveys", srcSurveyId));
        if (!srcSurveySnap.exists()) continue;
        const srcSurvey = srcSurveySnap.data() as any;
        if ((srcSurvey?.status ?? "unpublished") === "deleted") continue;

        const newTitle = pickUniqueName(srcSurvey?.title || "Untitled survey", takenSurveyNames);
        const remappedJson = remapSurveyListBindings(srcSurvey?.json || {}, listIdMap);
        const newSurveyRef = await addDoc(collection(db, "surveys"), {
          userId: uid,
          ownerId: uid,
          title: newTitle,
          json: remappedJson,
          status: "unpublished",
          published: false,
          roles: { [uid]: "owner" },
          memberIds: [uid],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          clonedFrom: srcSurveyId,
          clonedFromTemplate: true,
        });

        const created: Survey = {
          id: newSurveyRef.id,
          title: newTitle,
          updatedAt: new Date(),
          status: "unpublished",
          json: remappedJson,
          userId: uid,
          roles: { [uid]: "owner" },
          memberIds: [uid],
          effectiveRole: "owner",
          responseCount: 0,
        };
        createdSurveys.push(created);
        copiedSurveys += 1;
      }

      if (createdSurveys.length) {
        setSurveys((prev) =>
          [...createdSurveys, ...prev].sort((a, b) => {
            const ta = a.updatedAt?.getTime?.() ?? 0;
            const tb = b.updatedAt?.getTime?.() ?? 0;
            return tb - ta;
          })
        );
      }

      setPopulateMsg(
        `Copied ${copiedSurveys} survey(s), ${copiedLists} list(s), and ${copiedListItems} list item(s).`
      );
    } catch (e: any) {
      error("SurveyManagerList: populate test data failed", e);
      setPopulateErr(e?.message || "Failed to populate test data.");
    } finally {
      setPopulateBusy(false);
    }
  };

  // Publish toggle
  const togglePublish = async (id: string, current: SurveyStatus) => {
    if (current === "deleted") return;
    if (current === "published") {
      const allow = window.confirm(
        "Unpublish this survey? Respondents will no longer be able to access the public link."
      );
      if (!allow) return;
    }
    const next: SurveyStatus = current === "published" ? "unpublished" : "published";
    try {
      await updateDoc(doc(db, "surveys", id), {
        status: next,
        published: next === "published", // legacy support
        updatedAt: serverTimestamp(),
      });
      setSurveys((prev) =>
        prev.map((s) =>
          s.id === id ? { ...s, status: next, updatedAt: new Date() } : s
        )
      );
    } catch (e) {
      error("SurveyManagerList: togglePublish failed for", id, e);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch((err) => {
      error("SurveyManagerList: clipboard copy failed:", err);
    });
  };

  // Columns — includes Collaboration + role-aware actions
  const columns: GridColDef<Survey>[] = useMemo(
    () => [
      { field: "title", headerName: "Title", flex: 1.6, minWidth: 220 },
      {
        field: "updatedAt",
        headerName: "Last Updated",
        width: 180,
        renderCell: ({ row }) => {
          const dt = row.updatedAt as Date | undefined;
          return dt ? dt.toLocaleString() : "";
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        renderCell: ({ row }) =>
          row.status === "published" ? "✅ Published" : "🕘 Unpublished",
      },
      {
        field: "responseCount",
        headerName: "Responses",
        width: 110,
        renderCell: ({ row }) =>
          typeof row.responseCount === "number" ? row.responseCount : "—",
      },
      {
        field: "access",
        headerName: "Collaboration",
        width: 150,
        renderCell: ({ row }) => labelForRole(row.effectiveRole),
        sortable: false,
      },
      {
        field: "publicLink",
        headerName: "Public Link",
        width: 320,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          if (row.status !== "published") return null;
          const link = `${window.location.origin}/view/${row.id}`;
          return (
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <Typography
                variant="body2"
                noWrap
                sx={{ flex: 1, lineHeight: 1.2 }}
                title={link}
              >
                {link}
              </Typography>
              <Tooltip title="Copy link">
                <IconButton size="small" onClick={() => copyToClipboard(link)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 150,
        getActions: (params) => {
          const role = params.row.effectiveRole;
          const id = params.id as string;

          if (role === "owner" || role === "admin") {
            const actions = [
              <GridActionsCellItem
                key="manage"
                label="Manage"
                onClick={() => navigate(`/surveys/${id}`)}
                showInMenu
              />,
              <GridActionsCellItem
                key="edit"
                label="Edit"
                onClick={() => navigate(`/surveys/${id}/edit`)}
                showInMenu
              />,
              <GridActionsCellItem
                key="duplicate"
                label="Duplicate"
                onClick={() => openDuplicateDialog(params.row as Survey)}
                showInMenu
              />,
              ...(isAdmin
                ? [
                    <GridActionsCellItem
                      key="rename-stakeholders"
                      label="Rename stakeholders"
                      onClick={() => navigate(`/surveys/${id}/stakeholders`)}
                      showInMenu
                    />,
                  ]
                : []),
              <GridActionsCellItem
                key="delete"
                label="Delete"
                onClick={() => handleDelete(id)}
                showInMenu
              />,
              <GridActionsCellItem
                key="toggle"
                label={
                  (params.row as Survey).status === "published"
                    ? "Unpublish"
                    : "Publish"
                }
                onClick={() =>
                  togglePublish(id, (params.row as Survey).status as SurveyStatus)
                }
                showInMenu
              />,
              <GridActionsCellItem
                key="analytics"
                label="View Results"
                onClick={() => navigate(`/analytics/${id}`)}
                showInMenu
              />,
              <GridActionsCellItem
                key="dashboard"
                label="Dashboard"
                onClick={() => navigate(`/dashboard/${id}`)}
                showInMenu
              />,
            ];
            return actions;
          }

          if (role === "editor") {
            return [
              <GridActionsCellItem
                key="edit"
                label="Edit"
                onClick={() => navigate(`/surveys/${id}/edit`)}
                showInMenu
              />,
              <GridActionsCellItem
                key="analytics"
                label="View Results"
                onClick={() => navigate(`/analytics/${id}`)}
                showInMenu
              />,
              <GridActionsCellItem
                key="dashboard"
                label="Dashboard"
                onClick={() => navigate(`/dashboard/${id}`)}
                showInMenu
              />,
            ];
          }

          if (role === "viewer") {
            return [
              <GridActionsCellItem
                key="analytics"
                label="View Results"
                onClick={() => navigate(`/analytics/${id}`)}
                showInMenu
              />,
              <GridActionsCellItem
                key="dashboard"
                label="Dashboard"
                onClick={() => navigate(`/dashboard/${id}`)}
                showInMenu
              />,
            ];
          }

          // No role – no actions
          return [];
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin, showAll]
  );

  return (
    <Box sx={{ p: 2 }}>
      <SurveyHelpBanner />

      {errMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errMsg}
        </Alert>
      )}
      {populateErr && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {populateErr}
        </Alert>
      )}
      {populateMsg && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {populateMsg}
        </Alert>
      )}

      <PageHeader
        title="Surveys"
        subtitle="Create and manage your surveys"
        actions={
          isAdmin ? (
            <FormControlLabel
              control={<Switch checked={showAll} onChange={(_, v) => setShowAll(v)} />}
              label="Show all (admin)"
            />
          ) : undefined
        }
      />

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
        <TextField
          label="New survey name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          size="small"
        />
        <Button variant="contained" onClick={handleCreate} disabled={!newName.trim()}>
          Create Survey
        </Button>
        {testDataConfig.enabled && (
          <Button
            variant="outlined"
            onClick={handlePopulateTestData}
            disabled={populateBusy}
            sx={{ ml: "auto" }}
          >
            {populateBusy ? "Populating..." : "Populate Test Data"}
          </Button>
        )}
      </Box>

      <Box sx={{ width: "100%", overflowX: "auto", mt: 1 }}>
        <DataGrid
          loading={loading}
          rows={surveys}
          columns={columns}
          getRowId={(r) => r.id}
          pageSizeOptions={[5, 10, 20]}
          initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
          pagination
          autoHeight
          sx={{ "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" } }}
        />
      </Box>

      <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Button
          variant="text"
          color="inherit"
          size="small"
          onClick={() => navigate("/changelog")}
          sx={{ color: "text.secondary", textTransform: "none" }}
        >
          {appVersion}
        </Button>
        <Button
          variant="outlined"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => navigate("/deleted")}
        >
          View Deleted Items
        </Button>
      </Box>

      <Dialog open={duplicateOpen} onClose={() => setDuplicateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Duplicate survey</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="New survey name"
              fullWidth
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
              autoFocus
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeResponses}
                  onChange={(_, v) => setIncludeResponses(v)}
                />
              }
              label="Include responses"
            />
            {duplicateErr && (
              <Alert severity="error">{duplicateErr}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateOpen(false)} disabled={duplicating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDuplicate}
            disabled={duplicating}
          >
            {duplicating ? "Duplicating..." : "Duplicate"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
